import { NotifyHelper } from 'classes/helpers/notify.helper';
import { useMachineDetailsSubscription } from 'components/sections/admin-portal/machines/hooks/useMachineDetailsSubscription';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import {
  MachineErrorsQueryParams,
  MachineSettingsQueryParams,
  MachineSettingsSection,
  MachineShotHistoryQueryParams,
  MachineShotLiveFiresQueryParams,
} from 'enums/machine-settings';
import { IMachineSettingsURLParams } from 'interfaces/i-machine-settings';
import { IAdminMachine } from 'lib_ts/interfaces/i-machine';
import { keyBy, mapValues } from 'lodash';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const VERBOSE = false;

interface IProps {
  loadMachineDetails?: boolean;
}

/**
 * This hook does 3 main things:
 * - Abstracts the logic for setting and getting URL parameters as they relate to the MachineSettingsDialog
 * - Manages the visibility and navigation state of the MachineSettingsDialog via the above
 * - Loads machine details into the zustand store
 */
export const useMachineSettingsDialogState = (props?: IProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // e.g. ?machineID=Arc17&section=PitchReport&errorID=abc12345
  const machineID = searchParams.get(MachineSettingsQueryParams.MachineID);
  const sectionID = searchParams.get(
    MachineSettingsQueryParams.SectionID
  ) as MachineSettingsSection | null;

  const showMachineSettingsDialog = !!machineID;
  const activeSection = sectionID || MachineSettingsSection.Status;

  const machineDetailsInitialized = useMachinesPortalStore(
    (s) => s.machineDetailsInitialized
  );
  const loadingMachineDetails = useMachinesPortalStore(
    (s) => s.loadingMachineDetails
  );
  const shouldRefetchMachines = useMachinesPortalStore(
    (s) => s.shouldRefetchMachines
  );
  const resetMachineDetails = useMachinesPortalStore(
    (s) => s.resetMachineDetails
  );
  const setShouldRefetchMachines = useMachinesPortalStore(
    (s) => s.setShouldRefetchMachines
  );
  const fetchMachinesAndModels = useMachinesPortalStore(
    (s) => s.fetchMachinesAndModels
  );
  const initMachineDetails = useMachinesPortalStore(
    (s) => s.initMachineDetails
  );

  const { subscribeToDetails, unsubscribeFromDetails } =
    useMachineDetailsSubscription();

  useEffect(() => {
    if (machineID) {
      return;
    }

    closeMachineSettingsDialog();
  }, [machineID]);

  // loading machine details into zustand when using this hook is opt-in, so we'll skip unless explicitly requested
  useEffect(() => {
    if (!props?.loadMachineDetails) {
      return;
    }

    // dialog is closed or loading is already in progress/complete
    if (!machineID || machineDetailsInitialized || loadingMachineDetails) {
      return;
    }

    const loadMachine = async () => {
      const initializedMachine = await initMachineDetails(machineID);

      if (!initializedMachine) {
        // machine doesn't exist, clear params to reset state
        resetParams();
        NotifyHelper.error({
          message_md: `Machine "${machineID}" not found`,
        });
        return;
      }

      subscribeToDetails();
    };

    loadMachine();
  }, [
    machineID,
    machineDetailsInitialized,
    loadingMachineDetails,
    props?.loadMachineDetails,
  ]);

  const getParams = () => new URLSearchParams(window.location.search);

  const buildParams = (
    params: Partial<IMachineSettingsURLParams>,
    source: string
  ) => {
    const output = new URLSearchParams(window.location.search);

    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        output.delete(MachineSettingsQueryParams.ErrorLevel);
        value.forEach((v) =>
          output.append(MachineSettingsQueryParams.ErrorLevel, v)
        );
        return;
      }

      if (value) {
        output.set(key, value.toString());
        return;
      }

      output.delete(key);
    });

    if (VERBOSE) {
      console.debug(
        `useMachineSettingsDialogState.buildParams (${source})`,
        output.toString()
      );
    }

    return output;
  };

  const setParams = (
    params: Partial<IMachineSettingsURLParams>,
    source: string
  ) => {
    const nextParams = buildParams(params, source);

    if (VERBOSE) {
      console.debug(
        `useMachineSettingsDialogState.setParams (${source})`,
        nextParams.toString()
      );
    }
    setSearchParams(nextParams, { replace: true });
  };

  // remove all dialog related params from the URL
  const resetParams = () => {
    const updated = new URLSearchParams(window.location.search);

    Object.values(MachineSettingsQueryParams).forEach((v) => {
      updated.delete(v);
    });

    setSearchParams(updated, {
      replace: true,
    });
  };

  // opens the dialog to the Status section unless one is provided
  const openMachineSettingsDialog = (
    machine: IAdminMachine,
    section?: MachineSettingsSection
  ) => {
    setParams(
      {
        [MachineSettingsQueryParams.MachineID]: machine.machineID,
        [MachineSettingsQueryParams.SectionID]: section || activeSection,
      },
      'openMachineSettingsDialog'
    );
  };

  // removes the machineID and sectionID from the URL params and resets the machine field in the zustand store
  const closeMachineSettingsDialog = async () => {
    unsubscribeFromDetails();

    resetParams();
    resetMachineDetails();

    if (shouldRefetchMachines) {
      await fetchMachinesAndModels();
      setShouldRefetchMachines(false);
    }
  };

  const setActiveSection = (section: MachineSettingsSection) => {
    const safeSection = MachineSettingsSection[section]
      ? section
      : MachineSettingsSection.Status;

    setParams(
      {
        [MachineSettingsQueryParams.SectionID]: safeSection,
      },
      'setActiveSection'
    );
  };

  // removes only error related params from the URL
  const resetErrorParams = () => {
    const updated = mapValues(keyBy(MachineErrorsQueryParams), () => undefined);
    setParams(updated, 'resetErrorParams');
  };

  // removes only shot related params from the URL
  const resetShotHistoryParams = () => {
    const updated = mapValues(
      keyBy(MachineShotHistoryQueryParams),
      () => undefined
    );
    setParams(updated, 'resetShotHistoryParams');
  };

  // removes only live fire related params from the URL
  const resetShotLiveFiresParams = () => {
    const updated = mapValues(
      keyBy(MachineShotLiveFiresQueryParams),
      () => undefined
    );
    setParams(updated, 'resetShotLiveFiresParams');
  };

  // cleanup URL params when navigating away from the dialog or between sections
  useEffect(() => {
    if (activeSection && !machineID) {
      resetParams();
    }

    // reset all section specific fields if we navigate away from said section
    if (activeSection !== MachineSettingsSection.Errors) {
      resetErrorParams();
    }

    if (activeSection !== MachineSettingsSection.ShotHistory) {
      resetShotHistoryParams();
    }

    if (activeSection !== MachineSettingsSection.LiveFires) {
      resetShotLiveFiresParams();
    }
  }, [activeSection, machineID]);

  return {
    showMachineSettingsDialog,
    openMachineSettingsDialog,
    closeMachineSettingsDialog,
    activeSection,
    setActiveSection,
    getParams,
    buildParams,
    setParams,
  };
};
